import { DateOnly, DateOnlySchema } from '@/api/common/common.models';

import { getAxiosInstance } from '../api.client';
import {
    type PreliminaryCostAggregate,
    type PreliminaryCostEntry,
    type PreliminaryCostEntryFormModel,
    PreliminaryCostEntrySchema,
} from './preliminaryCostEntries.models';

export async function getPreliminaryCostEntries(
    projectId: string,
    preliminaryId: string,
): Promise<PreliminaryCostEntry[]> {
    const response = await getAxiosInstance().get<PreliminaryCostEntry[]>(
        `/project/${projectId}/preliminary/${preliminaryId}/cost-entry`,
    );
    return response.data;
}

export async function getPreliminaryCostEntryById(
    projectId: string,
    preliminaryId: string,
    id: string,
): Promise<PreliminaryCostEntry> {
    const response = await getAxiosInstance().get<PreliminaryCostEntry>(
        `/project/${projectId}/preliminary/${preliminaryId}/cost-entry/${id}`,
    );

    const parsedData = PreliminaryCostEntrySchema.safeParse(response.data);

    if (!parsedData.success) {
        throw new Error('Invalid cost entry data');
    }

    return response.data;
}

export async function createPreliminaryCostEntry(
    projectId: string,
    preliminaryId: string,
    data: PreliminaryCostEntryFormModel,
): Promise<PreliminaryCostEntry> {
    const response = await getAxiosInstance().postForm<PreliminaryCostEntry>(
        `/project/${projectId}/preliminary/${preliminaryId}/cost-entry`,
        data,
    );
    return response.data;
}

export async function updatePreliminaryCostEntry(
    projectId: string,
    preliminaryId: string,
    id: string,
    data: PreliminaryCostEntryFormModel,
): Promise<PreliminaryCostEntry> {
    const response = await getAxiosInstance().putForm<PreliminaryCostEntry>(
        `/project/${projectId}/preliminary/${preliminaryId}/cost-entry/${id}`,
        data,
    );
    return response.data;
}

export async function deletePreliminaryCostEntry(
    projectId: string,
    preliminaryId: string,
    id: string,
): Promise<void> {
    await getAxiosInstance().delete(
        `/project/${projectId}/preliminary/${preliminaryId}/cost-entry/${id}`,
    );
}

export async function getPreliminaryCostEntryDates(
    projectId: string,
    startDate: DateOnly,
    endDate: DateOnly,
): Promise<string[]> {
    const isValid =
        DateOnlySchema.safeParse(startDate).success &&
        DateOnlySchema.safeParse(endDate).success;
    if (!isValid) {
        throw new Error('Invalid date range');
    }

    const response = await getAxiosInstance().get<string[]>(
        `/project/${projectId}/preliminary-cost-entry-dates`,
        {
            params: {
                startDate,
                endDate,
            },
        },
    );
    return response.data;
}

export async function getPreliminaryCostEntriesByDate(
    projectId: string,
    date: DateOnly,
): Promise<PreliminaryCostAggregate[]> {
    const isValid = DateOnlySchema.safeParse(date).success;
    if (!isValid) {
        throw new Error('Invalid date');
    }

    const response = await getAxiosInstance().get<PreliminaryCostAggregate[]>(
        `/project/${projectId}/preliminary-cost-entries-by-date`,
        {
            params: {
                date,
            },
        },
    );
    return response.data;
}
